/**
 * engineService.js
 * Frontend API client for the Engine Analysis pipeline.
 * Abstracts polling and future SSE logic.
 */

import axios from 'axios';

// Get base URL depending on environment
const getBaseUrl = () => {
  const customHost = (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) || 
                     (typeof window !== 'undefined' && window._env_?.REACT_APP_API_URL);
  if (customHost) {
    return customHost.endsWith('/engine') ? customHost : `${customHost}/engine`;
  }
  return '/api/engine';
};

const API_URL = getBaseUrl();

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

/**
 * Initiates an Audit Job and returns the Job ID.
 */
export const startAudit = async (videoId) => {
  try {
    const response = await axios.post(`${API_URL}/audit`, { videoId }, getAuthHeaders());
    if (response.data.success) {
      return response.data;
    }
    throw new Error(response.data.error || 'Failed to start audit.');
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message);
  }
};

/**
 * Polls the job status endpoint.
 * In the future, this can be swapped with SSE without changing the UI component's event loop structure.
 */
export const checkJobStatus = async (jobId) => {
  try {
    const response = await axios.get(`${API_URL}/status/${jobId}`, getAuthHeaders());
    return response.data; // { success, job: { status, progress, stage, error }, data: dashboardDTO }
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message);
  }
};

/**
 * Cancels a pending or queued job.
 */
export const cancelJob = async (jobId) => {
  try {
    const response = await axios.post(`${API_URL}/cancel/${jobId}`, {}, getAuthHeaders());
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message);
  }
};

/**
 * Initiates a Channel Audit Job (Phase 1.1) and returns the Job ID.
 */
export const startChannelAudit = async (channelIdentifier) => {
  try {
    const response = await axios.post(`${API_URL}/audit-channel`, { channelIdentifier }, getAuthHeaders());
    if (response.data.success) {
      return response.data;
    }
    throw new Error(response.data.error || 'Failed to start channel audit.');
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message);
  }
};

/**
 * Initiates a Bulk Video Audit Job (Phase 1.3) for up to 10 videos.
 */
export const startBulkAudit = async (videoIds) => {
  try {
    const response = await axios.post(`${API_URL}/audit-bulk`, { videoIds }, getAuthHeaders());
    if (response.data.success) {
      return response.data;
    }
    throw new Error(response.data.error || 'Failed to start bulk audit.');
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message);
  }
};

/**
 * Polls status for multiple bulk job IDs in a single request.
 */
export const checkBulkJobStatus = async (jobIds) => {
  try {
    const response = await axios.post(`${API_URL}/status-bulk`, { jobIds }, getAuthHeaders());
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message);
  }
};

/**
 * Retrieves historical score trend data for a target video or channel (Phase 1.4).
 */
export const getHistoryTrends = async (targetId) => {
  try {
    const response = await axios.get(`${API_URL}/history/trends/${encodeURIComponent(targetId)}`, getAuthHeaders());
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message);
  }
};

